<script setup>
// Scenarios (phase 4.4): the kit's plant — Invitation, the kit's SuggestedRules with the example scenarios, the
// ListToolbar, the kit's ScenarioCard (through ScenarioCardSkin), the empty state over faded example cards (D-G5),
// and the scenario editor as a drawer (ScenarioEditorDrawer, D-G6 + Q1) instead of the popover and the in-place edit.
// Same store calls and messages as before.
// Fatia 1 of the editable script (doc 39): when the assistant has a script (scenario set), the page opens on "Max's
// script", a document of numbered steps (ScenarioScriptEditor), and the free scenarios keep their own segment, as
// they were. Without a script the free scenarios are the page, with a card to import a base script above them.
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-next/polaris/ui/tabs';
import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import SuggestedRules from 'dashboard/components-next/polaris/cards/SuggestedRules.vue';
import ListToolbar from 'dashboard/components-next/polaris/cards/ListToolbar.vue';
import ScenarioCardSkin from 'dashboard/components-next/polaris/skin/ScenarioCardSkin.vue';
import BulkSelectBar from 'dashboard/components-next/polaris/assistant/BulkSelectBar.vue';
import ScenarioEditorDrawer from 'dashboard/components-next/polaris/pageComponents/scenario/ScenarioEditorDrawer.vue';
import PolarisEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/PolarisEmptyState.vue';
import ScenarioScriptEditor from 'dashboard/components-next/polaris/pageComponents/scenario/ScenarioScriptEditor.vue';
import ScenarioImportCard from 'dashboard/components-next/polaris/pageComponents/scenario/ScenarioImportCard.vue';
import PlaybookDataSection from 'dashboard/components-next/polaris/pageComponents/scenario/PlaybookDataSection.vue';
import OutcomesSection from 'dashboard/components-next/polaris/pageComponents/scenario/OutcomesSection.vue';

const { t, tm, rt } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const assistantId = computed(() => Number(route.params.assistantId));

const uiFlags = useMapGetter('polarisScenarios/getUIFlags');
const isFetchingFree = computed(() => uiFlags.value.fetchingList);
const setFlags = useMapGetter('polarisScenarioSet/getUIFlags');
const hasSet = useMapGetter('polarisScenarioSet/hasSet');
const catalog = useMapGetter('polarisScenarioSet/getCatalog');
const setAssistantId = useMapGetter('polarisScenarioSet/getAssistantId');
// The set of another assistant, or one still coming, is not this page's: wait for it.
const setReady = computed(
  () => setAssistantId.value === assistantId.value && !setFlags.value.fetching
);
const isFetching = computed(() => isFetchingFree.value || !setReady.value);

// "script" (Max's script) or "free" (the free scenarios); only a set makes two segments.
const segment = ref('script');
const showScript = computed(() => hasSet.value && segment.value === 'script');
const showFree = computed(() => !showScript.value);
const scenarios = useMapGetter('polarisScenarios/getRecords');

const searchQuery = ref('');

const getToolsFromInstruction = instruction => [
  ...new Set(
    [...(instruction?.matchAll(/\(tool:\/\/([^)]+)\)/g) ?? [])].map(m => m[1])
  ),
];

// Suggested example scenarios for quick add (POLARIS.PAGES.SCENARIO_EXAMPLES, en and pt_BR)
const scenariosExample = computed(() =>
  tm('POLARIS.PAGES.SCENARIO_EXAMPLES').map((example, index) => {
    const instruction = rt(example.INSTRUCTION);
    return {
      id: index + 1,
      title: rt(example.TITLE),
      description: rt(example.DESCRIPTION),
      instruction,
      tools: getToolsFromInstruction(instruction),
    };
  })
);

const filteredScenarios = computed(() => {
  const query = searchQuery.value.trim();
  const source = scenarios.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'instruction']);
});

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_scenarios_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_scenarios_suggestions: false });
};

// Bulk selection
const bulkSelectedIds = ref(new Set());

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = scenarios.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('POLARIS.ASSISTANTS.SCENARIOS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('POLARIS.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('POLARIS.ASSISTANTS.SCENARIOS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const deleteScenario = async id => {
  try {
    await store.dispatch('polarisScenarios/delete', {
      id,
      assistantId: assistantId.value,
    });
    useAlert(t('POLARIS.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('POLARIS.ASSISTANTS.SCENARIOS.API.DELETE.ERROR');
    useAlert(errorMessage);
  }
};

// TODO: Add bulk delete endpoint
const bulkDeleteScenarios = async ids => {
  const idsArray = ids || Array.from(bulkSelectedIds.value);
  await Promise.all(
    idsArray.map(id =>
      store.dispatch('polarisScenarios/delete', {
        id,
        assistantId: assistantId.value,
      })
    )
  );
  bulkSelectedIds.value = new Set();
  useAlert(t('POLARIS.ASSISTANTS.SCENARIOS.API.DELETE.SUCCESS'));
};

const addScenario = async scenario => {
  try {
    await store.dispatch('polarisScenarios/create', {
      assistantId: assistantId.value,
      ...scenario,
      tools: getToolsFromInstruction(scenario.instruction),
    });
    useAlert(t('POLARIS.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('POLARIS.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

const addAllExampleScenarios = async () => {
  try {
    scenariosExample.value.forEach(async scenario => {
      await store.dispatch('polarisScenarios/create', {
        assistantId: assistantId.value,
        ...scenario,
      });
    });
    useAlert(t('POLARIS.ASSISTANTS.SCENARIOS.API.ADD.SUCCESS'));
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('POLARIS.ASSISTANTS.SCENARIOS.API.ADD.ERROR');
    useAlert(errorMessage);
  }
};

// The editor: a new scenario (null) or the one being edited.
const editorOpen = ref(false);
const editing = ref(null);
const editorDrawer = ref(null);

const openEditor = scenario => {
  const show = () => {
    editing.value = scenario;
    editorOpen.value = true;
  };
  // Switching rows with the drawer open asks first when there are unsaved changes (useDiscardGuard).
  if (editorOpen.value && editorDrawer.value) {
    editorDrawer.value.runGuarded(show);
  } else show();
};

// Fase 4: o drawer só cria (editar abre a página Fin-style, polaris_scenarios_edit).
const onEditorSave = scenario => {
  addScenario(scenario);
};

const handleAction = ({ action, id }) => {
  if (action === 'edit') {
    // Fase 4: editar abre o editor Fin-style em página cheia (a criação segue no drawer).
    router.push({
      name: 'polaris_scenarios_edit',
      params: {
        accountId: route.params.accountId,
        assistantId: assistantId.value,
        scenarioId: id,
      },
    });
  }
  if (action === 'delete') deleteScenario(id);
};

// The script: loaded with the page and again when the assistant changes. A failed load is "no script".
const loadSet = () =>
  store.dispatch('polarisScenarioSet/get', assistantId.value).catch(() => {});

const importScript = async key => {
  try {
    await store.dispatch('polarisScenarioSet/import', {
      assistantId: assistantId.value,
      key,
    });
    segment.value = 'script';
    useAlert(t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.API.IMPORT_SUCCESS'));
  } catch (error) {
    // the store rejects with the server's own sentence (already imported, unknown key, equivalence failed)
    useAlert(
      error?.message ||
        t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.API.IMPORT_ERROR')
    );
  }
};

// Leaving with a step not saved asks first (a route change, or another assistant from the picker), as Voice does.
const script = ref(null);
const scriptDirty = ref(false);
const leaveDialog = ref(null);
let pendingLeave = null;
const guard = next => {
  if (!scriptDirty.value) {
    next();
    return;
  }
  pendingLeave = next;
  leaveDialog.value?.open();
};
onBeforeRouteLeave((to, from, next) => guard(next));
onBeforeRouteUpdate((to, from, next) => guard(next));
const keepEditing = () => {
  pendingLeave?.(false);
  pendingLeave = null;
};
const discardSteps = () => {
  script.value?.discardAll();
  scriptDirty.value = false;
  const next = pendingLeave;
  pendingLeave = null;
  leaveDialog.value?.close();
  next?.();
};

watch(assistantId, () => {
  segment.value = 'script';
  loadSet();
});

onMounted(() => {
  store.dispatch('polarisScenarios/get', {
    assistantId: assistantId.value,
  });
  store.dispatch('polarisTools/getTools');
  // Uma vez só para as duas seções do Playbook (Dados a coletar e Desfechos leem os atributos de contato).
  store.dispatch('attributes/get');
  loadSet();
});
</script>

<template>
  <PageLayout
    :header-title="$t('POLARIS.ASSISTANTS.SCENARIOS.TITLE')"
    preview="open"
    :button-label="
      showFree ? $t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.CREATE') : ''
    "
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
    :invitation="
      showFree
        ? {
            title: $t('POLARIS.ASSISTANTS.SCENARIOS.TITLE'),
            text: $t('POLARIS.ASSISTANTS.SCENARIOS.DESCRIPTION'),
            actionLabel: $t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.CREATE'),
          }
        : null
    "
    invitation-key="scenarios"
    @click="openEditor(null)"
  >
    <template #body>
      <Tabs v-if="hasSet" v-model="segment" data-scenario-segments>
        <TabsList>
          <TabsTrigger value="script">
            {{ $t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.SEGMENT.SCRIPT') }}
          </TabsTrigger>
          <TabsTrigger value="free">
            {{ $t('POLARIS.ASSISTANTS.SCENARIOS.SCRIPT.SEGMENT.FREE') }}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <ScenarioScriptEditor
        v-if="showScript"
        ref="script"
        :assistant-id="assistantId"
        @update:dirty="value => (scriptDirty = value)"
      />
      <ScenarioImportCard
        v-else-if="!hasSet"
        :importable="catalog.importable"
        :importing="setFlags.importing"
        @import="importScript"
      />
      <SuggestedRules
        v-if="showFree && shouldShowSuggestedRules"
        :title="$t('POLARIS.ASSISTANTS.SCENARIOS.ADD.SUGGESTED.TITLE')"
        :items="scenariosExample"
        @close="closeSuggestedRules"
        @add="addScenario"
        @add-all="addAllExampleScenarios"
      >
        <template #default="{ item }">
          <div class="grid gap-1">
            <p class="type-polaris-body-medium">{{ item.title }}</p>
            <p class="type-polaris-body text-polaris-muted-foreground">
              {{ item.description }}
            </p>
          </div>
        </template>
      </SuggestedRules>

      <template v-if="showFree && scenarios.length">
        <ListToolbar
          v-if="bulkSelectedIds.size === 0"
          v-model:search="searchQuery"
          :placeholder="
            $t('POLARIS.ASSISTANTS.SCENARIOS.LIST.SEARCH_PLACEHOLDER')
          "
          :count="scenarios.length"
          count-key="POLARIS.PAGES.SCENARIOS_COUNT"
        />
        <BulkSelectBar
          v-else
          v-model="bulkSelectedIds"
          :all-items="scenarios"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          :delete-label="
            $t('POLARIS.ASSISTANTS.SCENARIOS.BULK_ACTION.BULK_DELETE_BUTTON')
          "
          @bulk-delete="bulkDeleteScenarios"
        />
        <PolarisEmptyState
          v-if="filteredScenarios.length === 0"
          :title="$t('POLARIS.ASSISTANTS.SCENARIOS.SEARCH_EMPTY_MESSAGE')"
        />
        <div v-else class="flex flex-col gap-3">
          <ScenarioCardSkin
            v-for="scenario in filteredScenarios"
            :key="scenario.id"
            :scenario="scenario"
            selectable
            :selected="bulkSelectedIds.has(scenario.id)"
            @select="handleRuleSelect"
            @action="handleAction"
          />
        </div>
      </template>
      <PolarisEmptyState
        v-else-if="showFree"
        :title="$t('POLARIS.ASSISTANTS.SCENARIOS.TITLE')"
        :description="$t('POLARIS.ASSISTANTS.SCENARIOS.EMPTY_MESSAGE')"
        :action-label="$t('POLARIS.ASSISTANTS.SCENARIOS.ADD.NEW.CREATE')"
        @click="openEditor(null)"
      >
        <template #backdrop>
          <ScenarioCardSkin
            v-for="example in scenariosExample"
            :key="example.id"
            :scenario="example"
          />
        </template>
      </PolarisEmptyState>

      <!-- Fase 3 (tela 02): Dados a coletar e Desfechos do Playbook, abaixo da lista de cenários -->
      <hr class="border-0 border-t border-solid border-polaris-border" />
      <PlaybookDataSection />
      <OutcomesSection :assistant-id="assistantId" />
    </template>

    <template #aside>
      <ScenarioEditorDrawer
        ref="editorDrawer"
        v-model:open="editorOpen"
        :scenario="editing"
        @save="onEditorSave"
      />
    </template>
    <Dialog
      ref="leaveDialog"
      type="alert"
      width="md"
      :title="$t('POLARIS.DRAWER.DISCARD_TITLE')"
      :description="$t('POLARIS.DRAWER.DISCARD_TEXT')"
      :cancel-button-label="$t('POLARIS.DRAWER.KEEP_EDITING')"
      :confirm-button-label="$t('POLARIS.DRAWER.DISCARD')"
      @confirm="discardSteps"
      @close="keepEditing"
    />
  </PageLayout>
</template>
